import {
  isAcademyBundleTier,
  type ClubPlanTier,
  type OrgOperationType,
  type SubscriptionTier,
} from '@court-sync/shared';

export const PRICING_SELECTION_KEY = 'courtsync_pricing_selection';

export type PricingProductLine = 'academy' | 'club' | 'both';

export interface PricingSelection {
  source: 'pricing';
  productLine: PricingProductLine;
  billingInterval: 'month' | 'year';
  planId?: string;
  planTier?: SubscriptionTier | ClubPlanTier | string;
  academyPlanTier?: SubscriptionTier;
  clubPlanTier?: ClubPlanTier;
  academyPlanId?: string;
  clubPlanId?: string;
}

function isSubscriptionTier(value: string | null): value is SubscriptionTier {
  return value === 'starter' || value === 'professional' || value === 'enterprise';
}

function isClubPlanTier(value: string | null): value is ClubPlanTier {
  return value === 'starter' || value === 'professional';
}

function normalizeClubTierFromParam(value: string | null): ClubPlanTier | undefined {
  if (isClubPlanTier(value)) return value;
  if (value === 'club') return 'starter';
  if (value === 'club_pro') return 'professional';
  return undefined;
}

export function parsePricingSelectionFromSearchParams(
  params: URLSearchParams,
): PricingSelection | null {
  const source = params.get('source');
  const productLine = params.get('productLine') as PricingProductLine | null;
  if (source !== 'pricing' || !productLine) {
    return null;
  }
  if (productLine !== 'academy' && productLine !== 'club' && productLine !== 'both') {
    return null;
  }

  const billingInterval = params.get('billingInterval') === 'month' ? 'month' : 'year';
  const planId = params.get('planId') ?? undefined;
  const planTierParam = params.get('planTier');
  const academyPlanTierParam = params.get('academyPlanTier');
  const clubPlanTierParam = params.get('clubPlanTier');

  if (productLine === 'both') {
    const academyPlanTier = isAcademyBundleTier(academyPlanTierParam)
      ? academyPlanTierParam
      : undefined;
    const clubPlanTier = normalizeClubTierFromParam(clubPlanTierParam);
    if (!academyPlanTier || !clubPlanTier) return null;
    return {
      source: 'pricing',
      productLine,
      billingInterval,
      academyPlanTier,
      clubPlanTier,
      academyPlanId: params.get('academyPlanId') ?? undefined,
      clubPlanId: params.get('clubPlanId') ?? undefined,
    };
  }

  if (productLine === 'club') {
    const clubPlanTier =
      normalizeClubTierFromParam(planTierParam) ?? normalizeClubTierFromParam(planId ?? null);
    if (!clubPlanTier) return null;
    return {
      source: 'pricing',
      productLine,
      billingInterval,
      planId,
      planTier: clubPlanTier,
      clubPlanTier,
    };
  }

  const academyPlanTier = isSubscriptionTier(planTierParam)
    ? planTierParam
    : isSubscriptionTier(planId)
      ? (planId as SubscriptionTier)
      : undefined;
  if (!academyPlanTier) return null;

  return {
    source: 'pricing',
    productLine,
    billingInterval,
    planId,
    planTier: academyPlanTier,
    academyPlanTier,
  };
}

export function persistPricingSelection(selection: PricingSelection): void {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(PRICING_SELECTION_KEY, JSON.stringify(selection));
}

export function readPricingSelection(): PricingSelection | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(PRICING_SELECTION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PricingSelection;
    if (parsed?.source !== 'pricing') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearPricingSelection(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(PRICING_SELECTION_KEY);
}

export function pricingSelectionToOperationType(
  productLine: PricingProductLine,
): OrgOperationType {
  return productLine;
}

/** Draft fields written before academy setup when the operator arrived with a selected plan. */
export function buildPricingFastTrackDraftPatch(selection: PricingSelection): {
  pricingFastTrack: true;
  pricingSelectionSource: 'pricing';
  orgOperationType: OrgOperationType;
  selectedPlanTier?: PricingSelection['academyPlanTier'];
  selectedClubPlanTier?: PricingSelection['clubPlanTier'];
  planTierManuallySelected: true;
  clubPlanTierManuallySelected: true;
  completedStepIds: string[];
  currentStepId: 'academy-details' | 'club-details';
  clubOperatingProfile?: { offerings: Array<'court_hire' | 'memberships' | 'inventory_hire'> };
} {
  return {
    pricingFastTrack: true,
    pricingSelectionSource: 'pricing',
    orgOperationType: selection.productLine,
    selectedPlanTier: selection.academyPlanTier,
    selectedClubPlanTier: selection.clubPlanTier,
    planTierManuallySelected: true,
    clubPlanTierManuallySelected: true,
    completedStepIds: [],
    currentStepId: selection.productLine === 'club' ? 'club-details' : 'academy-details',
    clubOperatingProfile:
      selection.productLine !== 'academy'
        ? { offerings: ['court_hire', 'memberships', 'inventory_hire'] }
        : undefined,
  };
}

export function applyPricingSelectionToSession(selection: PricingSelection): void {
  persistPricingSelection(selection);

  if (selection.productLine === 'both') {
    if (selection.academyPlanTier) {
      sessionStorage.setItem('selectedAcademyPlanId', selection.academyPlanTier);
    }
    if (selection.clubPlanTier) {
      sessionStorage.setItem('selectedClubPlanId', selection.clubPlanTier);
    }
    sessionStorage.removeItem('selectedPlanId');
  } else if (selection.productLine === 'club' && selection.clubPlanTier) {
    sessionStorage.setItem('selectedClubPlanId', selection.clubPlanTier);
    if (selection.planId) {
      sessionStorage.setItem('selectedPlanId', selection.planId);
    }
  } else if (selection.academyPlanTier) {
    sessionStorage.setItem('selectedAcademyPlanId', selection.academyPlanTier);
    if (selection.planId) {
      sessionStorage.setItem('selectedPlanId', selection.planId);
    }
  }

  sessionStorage.setItem('selectedBillingInterval', selection.billingInterval);
}
